window.AdminCareers = function AdminCareers({ showNotification }) {
  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [departmentFilter, setDepartmentFilter] = React.useState('All');
  const [statusFilter, setStatusFilter] = React.useState('All');

  // Modal State
  const [showModal, setShowModal] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [form, setForm] = React.useState({
    id: 0,
    job_code: '',
    title: '',
    department: 'Research & Development',
    employment_type: 'Full-Time',
    experience: '3+ Years',
    qualification: "Bachelor's / Master's degree in Mechanical Engineering",
    location: 'HQ Facility',
    salary: 'Negotiable / Industry Standard',
    summary: '',
    requirements: ["Degree in relevant discipline.", "Hands-on experience with CAD/PLC."],
    responsibilities: ["Develop technical designs.", "Collaborate with engineers."],
    vacancy_count: 1,
    closing_date: '',
    enable_apply: true,
    status: 'active'
  });

  const [reqInput, setReqInput] = React.useState('');
  const [respInput, setRespInput] = React.useState('');

  React.useEffect(() => {
    fetchJobs();
    const handleSync = () => fetchJobs();
    window.addEventListener('storage', handleSync);
    window.addEventListener('novatek_careers_updated', handleSync);
    return () => {
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('novatek_careers_updated', handleSync);
    };
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await window.apiClient.get('careers.php?admin=1');
      if (res.success) setJobs(res.positions || res.data || []);
    } catch (e) {
      if (window.CAREERS_DATA) {
        setJobs(window.CAREERS_DATA.positions.map(j => ({ ...j, job_code: j.id.toUpperCase(), status: 'active' })));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditMode(false);
    setForm({
      id: 0,
      job_code: 'JOB-0' + (jobs.length + 1),
      title: '',
      department: 'Research & Development',
      employment_type: 'Full-Time',
      experience: '3+ Years',
      qualification: "Bachelor's / Master's degree in Engineering",
      location: 'HQ Facility',
      salary: 'Competitive Industry Package',
      summary: '',
      requirements: [
        "Bachelor's / Master's degree in Mechanical or Control Systems Engineering.",
        "Proficiency in SolidWorks, ANSYS, Siemens S7-1500 PLC, or LabVIEW.",
        "Proven track record in high-precision industrial automation or test bench development."
      ],
      responsibilities: [
        "Develop high-quality mechanical designs or automation control strategies.",
        "Prepare documentation and test specifications.",
        "Collaborate with multi-disciplinary engineering teams."
      ],
      vacancy_count: 1,
      closing_date: '',
      enable_apply: true,
      status: 'active'
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (job) => {
    setEditMode(true);
    setForm({
      id: job.id,
      job_code: job.job_code || job.id,
      title: job.title,
      department: job.department,
      employment_type: job.employment_type || job.type || 'Full-Time',
      experience: job.experience,
      qualification: job.qualification || '',
      location: job.location || 'HQ Facility',
      salary: job.salary || 'Negotiable',
      summary: job.summary,
      requirements: job.requirements || [],
      responsibilities: job.responsibilities || [],
      vacancy_count: job.vacancy_count || 1,
      closing_date: job.closing_date || '',
      enable_apply: job.enable_apply !== undefined ? Boolean(job.enable_apply) : true,
      status: job.status || 'active'
    });
    setShowModal(true);
  };

  const handleSaveJob = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await window.apiClient.put('careers.php', form);
        showNotification(`Job opening '${form.title}' updated.`);
      } else {
        await window.apiClient.post('careers.php', form);
        showNotification(`Job opening '${form.title}' created.`);
      }
      setShowModal(false);
      fetchJobs();
    } catch (err) {
      showNotification(err.message || 'Failed to save job opening.', 'error');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const nextStatus = currentStatus === 'active' ? 'closed' : 'active';
    try {
      await window.apiClient.put(`careers.php?action=toggle_status`, { id, status: nextStatus });
      showNotification(`Vacancy status changed to ${nextStatus}.`);
      fetchJobs();
    } catch (err) {
      showNotification(err.message || 'Status toggle failed.', 'error');
    }
  };

  const handleDeleteJob = async (id, title) => {
    if (!window.confirm(`Delete job opening '${title}'?`)) return;
    try {
      await window.apiClient.delete('careers.php', { id });
      showNotification(`Job '${title}' deleted.`);
      fetchJobs();
    } catch (err) {
      showNotification(err.message || 'Deletion failed.', 'error');
    }
  };

  const handleAddRequirement = () => {
    if (!reqInput) return;
    setForm({ ...form, requirements: [...form.requirements, reqInput] });
    setReqInput('');
  };

  const handleRemoveRequirement = (idx) => {
    setForm({ ...form, requirements: form.requirements.filter((_, i) => i !== idx) });
  };

  const handleAddResponsibility = () => {
    if (!respInput) return;
    setForm({ ...form, responsibilities: [...form.responsibilities, respInput] });
    setRespInput('');
  };

  const handleRemoveResponsibility = (idx) => {
    setForm({ ...form, responsibilities: form.responsibilities.filter((_, i) => i !== idx) });
  };

  const filteredJobs = jobs.filter((j) => {
    const matchesDept = departmentFilter === 'All' || j.department === departmentFilter;
    const matchesStatus = statusFilter === 'All' || j.status === statusFilter;
    const matchesSearch = j.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          j.summary.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesDept && matchesStatus && matchesSearch;
  });

  return (
    <>
      <div className="space-y-6 animate-fadeIn">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="font-serif text-2xl font-bold text-slate-900">Careers & Job Vacancies Management</h1>
          <p className="font-sans text-xs text-slate-500">Post open positions, close vacancies, save drafts, and toggle public application buttons.</p>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold uppercase px-5 py-3 rounded-lg shadow transition-all flex items-center gap-2 cursor-pointer"
        >
          <span className="material-symbols-outlined text-lg">post_add</span>
          <span>Create New Job Opening</span>
        </button>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        
        {/* Search */}
        <div className="relative w-full md:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
          <input
            type="text"
            placeholder="Search job title or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:border-secondary font-mono"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded text-xs text-slate-800 outline-none font-mono"
          >
            <option value="All">All Departments</option>
            <option value="Research & Development">Research & Development</option>
            <option value="Automation Engineering">Automation Engineering</option>
            <option value="Aerospace Solutions">Aerospace Solutions</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded text-xs text-slate-800 outline-none font-mono"
          >
            <option value="All">All Statuses</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="closed">Closed</option>
          </select>
        </div>

      </div>

      {/* Jobs Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-sans">
          <thead className="bg-slate-50 text-slate-500 font-mono text-[10px] uppercase border-b border-slate-200">
            <tr>
              <th className="p-4">CODE</th>
              <th className="p-4">JOB TITLE & SUMMARY</th>
              <th className="p-4">DEPARTMENT</th>
              <th className="p-4">VACANCIES</th>
              <th className="p-4">STATUS</th>
              <th className="p-4 text-center">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 font-mono">
            {filteredJobs.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-slate-400 font-mono">No job openings found matching filter criteria.</td>
              </tr>
            ) : (
              filteredJobs.map((job) => (
                <tr key={job.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4 font-mono font-bold text-secondary">{job.job_code || job.id}</td>
                  <td className="p-4 max-w-md">
                    <div className="font-bold text-slate-900 text-sm font-sans">{job.title}</div>
                    <div className="text-slate-500 text-[11px] line-clamp-1 font-sans">{job.summary}</div>
                  </td>
                  <td className="p-4 font-mono text-slate-700">{job.department}</td>
                  <td className="p-4 font-mono text-center font-bold text-slate-800">{job.vacancy_count || 1}</td>
                  <td className="p-4">
                    <span className={`font-mono text-[10px] font-bold px-2.5 py-1 rounded uppercase ${
                      job.status === 'active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                      job.status === 'closed' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}>
                      {job.status}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleToggleStatus(job.id, job.status)}
                        className={`p-1.5 rounded transition-colors text-xs font-mono font-bold border cursor-pointer ${
                          job.status === 'active' ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                        }`}
                        title={job.status === 'active' ? 'Close Vacancy' : 'Reopen Vacancy'}
                      >
                        <span className="material-symbols-outlined text-sm">{job.status === 'active' ? 'lock' : 'lock_open'}</span>
                      </button>
                      <button onClick={() => handleOpenEditModal(job)} className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200 cursor-pointer">
                        <span className="material-symbols-outlined text-sm">edit</span>
                      </button>
                      <button onClick={() => handleDeleteJob(job.id, job.title)} className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200 cursor-pointer">
                        <span className="material-symbols-outlined text-sm">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
      </div>

      {/* Job Form Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden text-slate-800">
            
            <div className="p-5 bg-white border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-serif text-lg font-bold text-slate-900">
                {editMode ? 'Edit Job Opening Specification' : 'Post New Career Opportunity'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-800 p-1 cursor-pointer">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <form onSubmit={handleSaveJob} className="flex-grow flex flex-col overflow-hidden">
              <div className="p-6 overflow-y-auto space-y-4 text-xs flex-grow">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">JOB CODE *</label>
                        <input
                          type="text"
                          required
                          value={form.job_code}
                          onChange={(e) => setForm({ ...form, job_code: e.target.value })}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 font-mono outline-none focus:border-secondary"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">JOB TITLE *</label>
                        <input
                          type="text"
                          required
                          value={form.title}
                          onChange={(e) => setForm({ ...form, title: e.target.value })}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">DEPARTMENT</label>
                        <input
                          type="text"
                          value={form.department}
                          onChange={(e) => setForm({ ...form, department: e.target.value })}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary"
                        />
                      </div>

                      <div>
                        <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">EXPERIENCE</label>
                        <input
                          type="text"
                          value={form.experience}
                          onChange={(e) => setForm({ ...form, experience: e.target.value })}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary"
                        />
                      </div>

                      <div>
                        <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">STATUS</label>
                        <select
                          value={form.status}
                          onChange={(e) => setForm({ ...form, status: e.target.value })}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary font-sans"
                        >
                          <option value="active">Active (Published)</option>
                          <option value="draft">Save as Draft</option>
                          <option value="closed">Closed Vacancy</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">QUALIFICATION</label>
                      <input
                        type="text"
                        value={form.qualification}
                        onChange={(e) => setForm({ ...form, qualification: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary"
                      />
                    </div>

                    <div>
                      <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">JOB SUMMARY *</label>
                      <textarea
                        rows="4"
                        required
                        value={form.summary}
                        onChange={(e) => setForm({ ...form, summary: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 resize-none outline-none focus:border-secondary font-sans"
                      ></textarea>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    
                    {/* Dynamic Job Responsibilities */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                      <label className="block font-mono text-[10px] font-bold text-secondary uppercase">
                        KEY RESPONSIBILITIES LIST
                      </label>
                      <ul className="space-y-1.5 max-h-[140px] overflow-y-auto pr-1">
                        {!form.responsibilities || form.responsibilities.length === 0 ? (
                          <span className="block text-slate-400 font-mono text-[10px] italic">No responsibilities added yet.</span>
                        ) : (
                          form.responsibilities.map((resp, idx) => (
                            <li key={idx} className="flex items-center justify-between bg-white px-2.5 py-1.5 rounded border border-slate-200 text-xs text-slate-900">
                              <span className="truncate pr-2 font-mono text-[11px]">• {resp}</span>
                              <button type="button" onClick={() => handleRemoveResponsibility(idx)} className="text-red-500 hover:text-red-700 shrink-0 cursor-pointer">
                                <span className="material-symbols-outlined text-sm">close</span>
                              </button>
                            </li>
                          ))
                        )}
                      </ul>

                      <div className="flex gap-2 pt-1.5 border-t border-slate-200">
                        <input
                          type="text"
                          placeholder="Add responsibility item..."
                          value={respInput}
                          onChange={(e) => setRespInput(e.target.value)}
                          className="flex-grow px-2.5 py-1.5 bg-white border border-slate-300 rounded text-slate-900 text-[11px] outline-none"
                        />
                        <button type="button" onClick={handleAddResponsibility} className="bg-slate-200 hover:bg-slate-300 text-slate-800 font-mono px-3 py-1 rounded font-bold shrink-0 transition-colors cursor-pointer">
                          Add
                        </button>
                      </div>
                    </div>

                    {/* Dynamic Job Requirements */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                      <label className="block font-mono text-[10px] font-bold text-secondary uppercase">
                        REQUIRED SKILLS LIST
                      </label>
                      <ul className="space-y-1.5 max-h-[140px] overflow-y-auto pr-1">
                        {!form.requirements || form.requirements.length === 0 ? (
                          <span className="block text-slate-400 font-mono text-[10px] italic">No skills added yet.</span>
                        ) : (
                          form.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-center justify-between bg-white px-2.5 py-1.5 rounded border border-slate-200 text-xs text-slate-900">
                              <span className="truncate pr-2 font-mono text-[11px]">• {req}</span>
                              <button type="button" onClick={() => handleRemoveRequirement(idx)} className="text-red-500 hover:text-red-700 shrink-0 cursor-pointer">
                                <span className="material-symbols-outlined text-sm">close</span>
                              </button>
                            </li>
                          ))
                        )}
                      </ul>

                      <div className="flex gap-2 pt-1.5 border-t border-slate-200">
                        <input
                          type="text"
                          placeholder="Add skill requirement item..."
                          value={reqInput}
                          onChange={(e) => setReqInput(e.target.value)}
                          className="flex-grow px-2.5 py-1.5 bg-white border border-slate-300 rounded text-slate-900 text-[11px] outline-none"
                        />
                        <button type="button" onClick={handleAddRequirement} className="bg-slate-200 hover:bg-slate-300 text-slate-800 font-mono px-3 py-1 rounded font-bold shrink-0 transition-colors cursor-pointer">
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Form Footer Action Buttons */}
              <div className="p-4 bg-slate-50 rounded-xl border-t border-slate-200 flex items-center justify-between shrink-0">
                <label className="flex items-center gap-2 cursor-pointer font-mono text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.enable_apply}
                    onChange={(e) => setForm({ ...form, enable_apply: e.target.checked })}
                    className="h-4 w-4 rounded accent-orange-500"
                  />
                  <span>Enable Public Apply Button</span>
                </label>

                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 font-mono rounded cursor-pointer text-xs">
                    Cancel
                  </button>
                  <button type="submit" className="px-6 py-2 bg-secondary hover:bg-orange-600 text-white font-mono font-bold rounded shadow cursor-pointer transition-all text-xs">
                    {editMode ? 'Save Changes' : 'Publish Job'}
                  </button>
                </div>
              </div>

            </form>

          </div>
        </div>
      )}
    </>
  );
};
